import * as React from "react";
import { useOpeningBalancesMatrixRange } from "@/hooks/finance/reconciliation/useStartingBalance";
import { useTxInOutMatrixRange } from "@/hooks/finance/reconciliation/useBankTransactionsInAndOut";

type Args = {
  bankAccountIds: string[];
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
};

export type ExpectedClosingRow = {
  expected_closing: number | null;

  opening_balance: number | null;
  opening_balance_ts: string | null;

  in_amount: number;
  out_amount: number;
  fee_total: number;

  currency: string | null;
};

export type ExpectedClosingMatrix = {
  dateKeys: string[];
  byAccount: Map<string, Map<string, ExpectedClosingRow>>;
};

export function useExpectedClosingMatrixRange(args: Args) {
  const { bankAccountIds, dateFrom, dateTo } = args;

  const openingQ = useOpeningBalancesMatrixRange(args);

  const txQ = useTxInOutMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled:
      bankAccountIds.length > 0 &&
      !!dateFrom &&
      !!dateTo &&
      !!openingQ.data,
  });

  const data = React.useMemo<ExpectedClosingMatrix | undefined>(() => {
    if (!openingQ.data || !txQ.data) return undefined;

    const dateKeys = openingQ.data.dateKeys;
    const byAccount = new Map<string, Map<string, ExpectedClosingRow>>();

    for (const accId of bankAccountIds) {
      const rowMap = new Map<string, ExpectedClosingRow>();
      const openMap = openingQ.data.byAccount.get(accId);
      const txMap = txQ.data.byAccount.get(accId);

      for (const dk of dateKeys) {
        const open = openMap?.get(dk);
        const tx = txMap?.get(dk);

        const opening_balance = open?.opening_balance ?? null;
        const in_amount = tx?.in_amount ?? 0;
        const out_amount = tx?.out_amount ?? 0;
        const fee_total = tx?.fee_total ?? 0;

        const expected_closing =
          opening_balance == null
            ? null
            : opening_balance + in_amount - out_amount - fee_total;

        rowMap.set(dk, {
          expected_closing,
          opening_balance,
          opening_balance_ts: open?.opening_balance_ts ?? null,
          in_amount,
          out_amount,
          fee_total,
          currency: open?.currency ?? tx?.currency ?? null,
        });
      }

      byAccount.set(accId, rowMap);
    }

    return { dateKeys, byAccount };
  }, [openingQ.data, txQ.data, bankAccountIds]);

  return {
    data,
    isLoading: openingQ.isLoading || txQ.isLoading,
    isError: openingQ.isError || txQ.isError,
    error: openingQ.error ?? txQ.error ?? null,
    refetch: async () => {
      await Promise.all([openingQ.refetch(), txQ.refetch()]);
    },
  };
}